import { FlexColumn, FlexRow, LabeledInput, StyledButton, useLoading, useModal } from '@gorila-shared-ui/components';
import { HeadingSmall, ParagraphMedium } from 'baseui/typography';
import { useEffect } from 'react';
import { areEqual } from 'react-window';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES } from '../../../constants/app';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import { deleteSubAccount, postSubAccount, updateSubAccount } from '../../../services/subAccountsService';
import { apiParamsState } from '../../../storage/app';
import {
  editingSubAccountState,
  selectedSubAccountIdState,
  subAccountState,
  updateSubAccountsState,
} from '../../../storage/subAccounts';
import { SubAccount } from '../../../types/subAccount';
import { AssetListPicker } from '../../shared/assetListPicker/AssetListPicker';
import ConfirmModal from '../../shared/ConfirmModal';

export function SubAccountDetail() {
  const { theme, css } = useStyles();
  const { openModal, closeModal, showModal } = useModal();
  const { loading, startLoading, stopLoading } = useLoading();
  const [editingSubAccount, setEditingSubAccount] = useRecoilState(editingSubAccountState);
  const resetEditingSubAccount = useResetRecoilState(editingSubAccountState);
  const apiParams = useRecoilValue(apiParamsState);
  const [selectedSubAccountId, setSelectedSubAccountId] = useRecoilState(selectedSubAccountIdState);
  const subAccount = useRecoilValue(subAccountState(selectedSubAccountId || ''));
  const resetSelectedSubAccountId = useResetRecoilState(selectedSubAccountIdState);
  const setUpdateSubAccounts = useSetRecoilState(updateSubAccountsState);
  const isNewSubAccount = editingSubAccount?._id === '';
  const { showDeletionFeedback, showFailFeedback, showPositiveFeedback } = useFeedback();

  useEffect(() => {
    resetEditingSubAccount();
    return () => {
      resetEditingSubAccount();
    };
  }, [selectedSubAccountId]);

  if (editingSubAccount === undefined) return null;

  const canSubmit = !!editingSubAccount.name && !!editingSubAccount.assets.length;
  const hasUpdates = !areEqual(subAccount || {}, editingSubAccount);

  const onDeleteSubAccount = async () => {
    if (!apiParams || selectedSubAccountId === undefined) return;
    const { error } = await deleteSubAccount(apiParams.clientId, apiParams.subClientId, selectedSubAccountId);
    if (!error) {
      showDeletionFeedback(FEEDBACK.deleted(FEEDBACK_PREFIXES.subAccount, subAccount?.name));
      setUpdateSubAccounts('update');
      resetSelectedSubAccountId();
      closeModal();
    } else {
      showFailFeedback(error || FEEDBACK.failedDeletion(FEEDBACK_PREFIXES.subAccount, subAccount?.name));
    }
  };

  const onInputChange = (value: string | boolean | number | null, field: keyof SubAccount) => {
    setEditingSubAccount((prev) => ({ ...prev!, [field]: value }));
  };

  const onSaveSubAccount = async () => {
    startLoading();
    const { id, error } = isNewSubAccount
      ? await postSubAccount(editingSubAccount)
      : await updateSubAccount(editingSubAccount);
    if (!error && id) {
      if (isNewSubAccount) setSelectedSubAccountId(id);
      showPositiveFeedback(
        isNewSubAccount
          ? FEEDBACK.created(FEEDBACK_PREFIXES.subAccount)
          : FEEDBACK.edited(FEEDBACK_PREFIXES.subAccount, editingSubAccount.name)
      );
      setUpdateSubAccounts(id);
    } else {
      showFailFeedback(
        error || isNewSubAccount
          ? FEEDBACK.failedCreation(FEEDBACK_PREFIXES.subAccount)
          : FEEDBACK.failedEdition(FEEDBACK_PREFIXES.subAccount, subAccount?.name)
      );
    }
    stopLoading();
  };

  const onAssetsSelectionChange = (assetIds: string[]) => {
    setEditingSubAccount((prev) => ({ ...prev!, assets: assetIds }));
  };

  return (
    <FlexColumn
      classNames={css({
        flex: 1,
        [theme.mediaQuery.large]: {
          maxWidth: '60%',
        },
        [theme.mediaQuery.extraLarge]: {
          maxWidth: '50%',
        },
      })}
    >
      <HeadingSmall margin={0}>{isNewSubAccount ? 'Nueva subcuenta' : subAccount?.name}</HeadingSmall>
      <FlexColumn classNames={css({ flex: 1 })}>
        <LabeledInput
          label="Nombre"
          value={editingSubAccount.name}
          onChange={(value) => onInputChange(value, 'name')}
          required
        />
        <AssetListPicker
          assignedAssetIds={editingSubAccount.assets}
          onAssignedChange={onAssetsSelectionChange}
          required
        />
      </FlexColumn>
      <FlexRow
        classNames={`${css({ alignItems: 'center', justifyContent: isNewSubAccount ? 'flex-end' : 'space-between' })}`}
      >
        {!isNewSubAccount && (
          <StyledButton
            kind="tertiary"
            onClick={openModal}
          >
            Eliminar
          </StyledButton>
        )}
        <FlexRow gap={theme.sizing.scale300}>
          <StyledButton
            kind="tertiary"
            onClick={resetSelectedSubAccountId}
          >
            Cancelar
          </StyledButton>
          <StyledButton
            onClick={onSaveSubAccount}
            isLoading={loading}
            disabled={!canSubmit || !hasUpdates}
          >
            Guardar
          </StyledButton>
        </FlexRow>
      </FlexRow>
      <ConfirmModal
        isOpen={showModal}
        onCancel={closeModal}
        onConfirm={onDeleteSubAccount}
        confirmText="Eliminar"
        description={<ParagraphMedium>¿Desea eliminar "{subAccount?.name}"?</ParagraphMedium>}
      />
    </FlexColumn>
  );
}
